import { toast } from 'react-toastify';
import PhishingIcon from '../../../Layout/Components/PhishingIcon';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, CardBody, Spinner } from 'reactstrap';
import { withTranslation } from 'react-i18next';
import PageTitle from '../../../Layout/Components/PageTitle';
import { getSimulationTemplateList, getGroupList, getSMTPConfigList, createPhishingCampaign } from '../../../API/PhishingAPI';
import TemplateGallery from '../TemplateGallery/TemplateGallery';
import { createBlankTemplate } from '../TemplateGallery/templateSets';
import { templateOrganization } from '../TemplateGallery/templateOrganization';
import UsersAndGroupsModal from '../UsersAndGroups/UsersAndGroupsModal';
import SMTPConfigModal from '../SMTPConfig/SMTPConfigModal';
import ChoiceDeleteModal from './ChoiceDeleteModal';
import TemplateImportPicker from './TemplateImportPicker';
import SimulationsLaunchScreenForm from './SimulationsLaunchScreenForm';
import { TemplatePicker, GroupPicker, SMTPPicker, SimulationIcon } from './SimulationPickers';
import useChoices from '../useChoices';
import './Simulations.css';

const emptySets = [];

export function Simulations({ t, history }) {
  const templates = useChoices(getSimulationTemplateList);
  const groups = useChoices(getGroupList);
  const profiles = useChoices(getSMTPConfigList);
  const [step, setStep] = useState(0);
  const [template, setTemplate] = useState(null);
  const [group, setGroup] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profilePickerOpen, setProfilePickerOpen] = useState(true);
  const [profileDraft, setProfileDraft] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [templateDraft, setTemplateDraft] = useState(null);
  const [groupDraft, setGroupDraft] = useState(null);
  const [visitedLaunch, setVisitedLaunch] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState('');
  const launchPending = useRef(false);
  const heading = useRef(null);
  const detailsHeading = useRef(null);
  const previousStep = useRef(step);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (step !== previousStep.current) heading.current?.focus();
    previousStep.current = step;
  }, [step]);
  useEffect(() => {
    if (step === 2 && profile && !profilePickerOpen) detailsHeading.current?.focus();
  }, [step, profile, profilePickerOpen]);

  function selectTemplate(item) {
    if (!item?.page || !item.message) return;
    setTemplate(item); setStep(1); setLaunchError('');
  }
  function selectGroup(item) {
    if (!item?.targets?.length) return;
    setGroup(item); setVisitedLaunch(true); setStep(2); setLaunchError('');
  }
  function templateSaved(item) {
    templates.saved(item); setTemplateDraft(null); setLibraryOpen(false); setTemplate(item); setLaunchError('');
    if (step === 0) setStep(1);
  }
  function groupSaved(item) {
    groups.saved(item); setGroupDraft(null); selectGroup(item);
  }
  function selectProfile(item) { setProfile(item); setProfilePickerOpen(false); setLaunchError(''); }
  function profileSaved(item) { profiles.saved(item); setProfileDraft(null); selectProfile(item); }
  function deleted(kind, item) {
    ({ templates, groups, smtp: profiles })[kind].removed(item.id);
    const same = selection => selection && String(selection.id) === String(item.id);
    if (kind === 'templates' && same(template)) { setTemplate(null); setStep(0); }
    if (kind === 'groups' && same(group)) { setGroup(null); setStep(1); }
    if (kind === 'smtp' && same(profile)) { setProfile(null); setProfilePickerOpen(true); }
    setLaunchError(''); setDeleteTarget(null);
  }
  async function launch(campaign) {
    if (launchPending.current || !template?.page || !group?.targets?.length || !profile?.id) return;
    launchPending.current = true; setLaunching(true); setLaunchError('');
    try {
      const result = await createPhishingCampaign({ ...campaign, simulation_template_id: template.id, groups: [{ name: group.name }], smtp: { name: profile.name } });
      if (mounted.current) {
        if (result?.schedule_adjusted) toast.info(t('simulation_wizard.schedule_adjusted', {
          date: new Date(result.launch_date).toLocaleString(), zone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }));
        history.push('/organization/phishing_simulator/phishing_campaign');
      }
    } catch (error) {
      if (mounted.current) setLaunchError(t('errors.creating', { element: t('simulation_wizard.title'), message: error.message || '' }));
    } finally {
      launchPending.current = false;
      if (mounted.current) setLaunching(false);
    }
  }
  const data = step === 0 ? templates : step === 1 ? groups : profiles;
  const stepKeys = ['templates', 'groups', 'launch'];
  const label = template ? templateOrganization(template, t).title : '';
  return <div className="simulation-wizard">
    <PageTitle heading={t('simulation_wizard.title')} subheading={t('simulation_wizard.subtitle')} icon={<PhishingIcon name="campaigns" />} />
    <Card className="simulation-wizard__shell"><CardBody>
      <nav aria-label={t('simulation_wizard.steps')} className="simulation-wizard__steps">
        {stepKeys.map((key, index) => <button type="button" key={key} disabled={launching || (index > 0 && !template) || (index > 1 && !group)}
          aria-current={step === index ? 'step' : undefined} className={`${index === step ? 'is-current' : index < step ? 'is-complete' : ''}`}
          onClick={() => { setStep(index); if (index === 2) setVisitedLaunch(true); }}>
          <span className="simulation-wizard__step-number" aria-hidden="true">{index < step ? '✓' : index + 1}</span>
          <span>{t(`simulation_wizard.step_${key}`)}</span>
        </button>)}
      </nav>
      {template && step > 0 && <div className="simulation-wizard__summary" aria-label={t('simulation_wizard.selected')}>
        <div><SimulationIcon /><span><small>{t('simulation_wizard.step_templates')}</small><strong>{label}</strong></span>
          <button type="button" disabled={launching} aria-label={t('simulation_wizard.change_template')} onClick={() => setStep(0)}>{t('simulation_wizard.change')}</button>
          <button type="button" disabled={launching} aria-label={t('simulation_wizard.edit_template', { name: label })} onClick={() => setTemplateDraft(template)}>{t('simulation_wizard.edit')}</button></div>
        {group && <div><SimulationIcon type="people" /><span><small>{t('simulation_wizard.member_count', { count: group.targets.length })}</small><strong>{group.name}</strong></span>
          <button type="button" disabled={launching} aria-label={t('simulation_wizard.change_group')} onClick={() => setStep(1)}>{t('simulation_wizard.change')}</button>
          <button type="button" disabled={launching} aria-label={t('simulation_wizard.edit_group', { name: group.name })} onClick={() => setGroupDraft({ group })}>{t('simulation_wizard.edit')}</button></div>}
        {profile && group && <div><SimulationIcon type="server" /><span><small>{t('smtp_config.title')}</small><strong>{profile.name}</strong></span>
          <button type="button" disabled={launching} aria-label={t('simulation_wizard.change_smtp')} onClick={() => { setStep(2); setProfilePickerOpen(true); }}>{t('simulation_wizard.change')}</button>
          <button type="button" disabled={launching} aria-label={t('simulation_wizard.edit_smtp', { name: profile.name })} onClick={() => setProfileDraft({ profile })}>{t('simulation_wizard.edit')}</button></div>}
      </div>}
      <div className="simulation-wizard__step-heading">
        <div><span className="simulation-wizard__eyebrow">{t('simulation_wizard.step_count', { current: step + 1, total: 3 })}</span>
          <h2 ref={heading} tabIndex={-1}>{t(`simulation_wizard.heading_${stepKeys[step]}`)}</h2>
          <p>{t(`simulation_wizard.help_${stepKeys[step]}`)}</p></div>
        <Button color="primary" disabled={launching || !data.items} onClick={() => step === 0 ? setLibraryOpen(true) : step === 1 ? setGroupDraft({}) : setProfileDraft({})}>
          + {t(step === 0 ? 'template_gallery.create_set' : step === 1 ? 'users_and_groups.actions.create' : 'smtp_config.actions.create')}
        </Button>
      </div>
      {step < 2 && <>
        {data.error ? <Alert color="danger" role="alert">{t('simulation_wizard.load_error')}
          <Button color="link" onClick={data.load}>{t('template_gallery.retry')}</Button></Alert>
          : !data.items ? <div className="simulation-wizard__loading" role="status"><Spinner size="sm" /> {t('simulation_wizard.loading')}</div>
            : step === 0 ? <TemplatePicker items={templates.items} selected={template} onSelect={selectTemplate} onEdit={setTemplateDraft}
              onDelete={item => setDeleteTarget({ kind: 'templates', item })} onCreate={() => setLibraryOpen(true)} t={t} />
              : <GroupPicker items={groups.items} selected={group} onSelect={selectGroup} onEdit={item => setGroupDraft({ group: item })}
                onDelete={item => setDeleteTarget({ kind: 'groups', item })} onCreate={() => setGroupDraft({})} t={t} />}
      </>}
      {visitedLaunch && <div hidden={step !== 2}>
        {launchError && <Alert color="danger" role="alert">{launchError}</Alert>}
        {(profilePickerOpen || !profile) && (profiles.error ? <Alert color="danger" role="alert">{t('simulation_wizard.load_error')}
          <Button color="link" onClick={profiles.load}>{t('template_gallery.retry')}</Button></Alert>
          : !profiles.items ? <p role="status"><Spinner tag="span" size="sm" /> {t('simulation_wizard.loading')}</p>
            : <SMTPPicker items={profiles.items} selected={profile} onSelect={selectProfile} onEdit={item => setProfileDraft({ profile: item })}
              onDelete={item => setDeleteTarget({ kind: 'smtp', item })} onCreate={() => setProfileDraft({})} t={t} />)}
        <div hidden={!profile || profilePickerOpen} className="simulation-wizard__launch-details">
          <h3 ref={detailsHeading} tabIndex={-1}>{t('simulation_wizard.details_title')}</h3>
          <SimulationsLaunchScreenForm onLaunch={launch} launching={launching} smtpProfile={profile} recipients={group?.targets || []} />
        </div>
      </div>}
      {step > 0 && <div className="simulation-wizard__footer"><Button color="link" disabled={launching} onClick={() => setStep(step - 1)}>← {t('simulation_wizard.back')}</Button></div>}
    </CardBody></Card>
    {libraryOpen && <TemplateImportPicker onClose={() => setLibraryOpen(false)} onBlank={() => setTemplateDraft(createBlankTemplate(t))}
      onImported={templateSaved} t={t} />}
    {templateDraft && <TemplateGallery editorOnly sets={emptySets} draft={templateDraft} onClose={() => setTemplateDraft(null)} onImported={templateSaved}
      primaryActionLabel={t('simulation_wizard.save_template')} contextHelp={t('simulation_wizard.template_edit_help')} />}
    {groupDraft && <UsersAndGroupsModal open showOpenButton={false} group={groupDraft.group} onClose={() => setGroupDraft(null)} onSubmit={groupSaved}
      submitLabel={t('simulation_wizard.save_group')} contextHelp={t('simulation_wizard.group_edit_help')} />}
    {profileDraft && <SMTPConfigModal open showOpenButton={false} smtpConfig={profileDraft.profile} onClose={() => setProfileDraft(null)} onSubmit={profileSaved}
      submitLabel={t('simulation_wizard.save_smtp')} contextHelp={t('simulation_wizard.smtp_edit_help')} />}
    {deleteTarget && <ChoiceDeleteModal target={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={deleted} t={t} />}
  </div>;
}

export default withTranslation()(Simulations);
